'use client'

import Image from 'next/image'
import { ReactNode, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'

/**
 * @file FileUpload.tsx
 * @description A versatile file upload component that supports single or multiple file uploads,
 * displaying existing images, and handling file deletions. It integrates with TanStack Form
 * and provides a user-friendly drag-and-drop interface.
 */
import { AnimatePresence, motion } from 'framer-motion'
import { Upload, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { ImagePreview } from '@/components/ui/image-preview'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

/**
 * Type definition for an image that already exists on the server.
 */
export type ExistingImage = { id: number | string; image: string }

/**
 * The value type that this form field component works with.
 * It can be a single File, an array of Files, or null.
 */
export type FileUploadValue = File | File[] | null | undefined

type Radius = 'full' | 'lg' | 'md' | 'sm'

const radiusClasses: Record<Radius, string> = {
  full: 'rounded-full',
  lg: 'rounded-lg',
  md: 'rounded-md',
  sm: 'rounded-sm'
}

type Size = '2xl' | '3xl' | 'lg' | 'md' | 'sm' | 'xl'

const sizeClasses: Record<Size, string> = {
  '2xl': 'h-80 w-80',
  '3xl': 'h-100 w-100',
  lg: 'h-40 w-40',
  md: 'h-20 w-20',
  sm: 'h-14 w-14',
  xl: 'h-60 w-60'
}

// Numeric values for sizes (in pixels) for calculations and styling
const sizeValues: Record<Size, number> = {
  '2xl': 320,
  '3xl': 400,
  lg: 160,
  md: 80,
  sm: 56,
  xl: 240
}

export const FileUpload = ({
  allowDelete = true,
  /**
   * An array of images that are already uploaded and exist on the server.
   * The component will display these images alongside any new files the user selects.
   */
  existingImages = [],
  fullWidth = false,
  height = 200,
  icon,
  isLoading = false,
  label,
  maxFiles = 1,
  /**
   * A callback function that is invoked when an existing image is deleted.
   * It receives the ID of the deleted image.
   */
  onDeleteExistingImage,
  radius = 'md',
  required,
  size = 'md'
}: {
  allowDelete?: boolean
  existingImages?: ExistingImage[]
  fullWidth?: boolean
  height?: number
  icon?: ReactNode
  isLoading?: boolean
  label?: string
  maxFiles?: number
  onDeleteExistingImage?: (id: number | string) => void
  radius?: Radius
  required?: boolean
  size?: Size
}) => {
  const t = useTranslations('components.form.fileUpload')
  // Get the field context from the parent TanStack Form.
  const field = useFieldContext<FileUploadValue>()
  const isInvalid = isFieldInvalid(field)

  /**
   * State to keep track of the IDs of existing images that the user has removed.
   * This allows us to hide them from the UI without immediately removing them from the parent form state.
   */
  const [removedExistingImages, setRemovedExistingImages] = useState<
    (number | string)[]
  >([])

  // Memoize the stringified version of existingImages to use as a stable dependency in useEffect.
  const stringifiedExistingImages = JSON.stringify(existingImages)

  // Reset removed images when the existing set changes (e.g. after submit and refetch)
  // so deleted images don't stay hidden if they reappear in props.
  const [prevExistingImages, setPrevExistingImages] = useState(
    stringifiedExistingImages
  )
  if (prevExistingImages !== stringifiedExistingImages) {
    setPrevExistingImages(stringifiedExistingImages)
    setRemovedExistingImages([])
  }

  /**
   * Memoized list of existing images that are currently visible.
   * It filters out any images that the user has marked for removal.
   */
  const visibleExistingImages = useMemo(
    () =>
      existingImages.filter(
        (image) => !removedExistingImages.includes(image.id)
      ),
    [existingImages, removedExistingImages]
  )

  /**
   * Memoized list of new files that the user has selected but have not yet been uploaded.
   * These are extracted from the field's value.
   */
  const newFiles = useMemo((): File[] => {
    const { value } = field.state
    if (Array.isArray(value)) {
      return value
    }
    if (value instanceof File) {
      return [value]
    }
    return []
  }, [field.state])

  /**
   * Separate PDFs from images
   */
  const pdfFiles = useMemo(() => {
    return newFiles.filter((file) => file.type === 'application/pdf')
  }, [newFiles])

  const imageFiles = useMemo(() => {
    return newFiles.filter((file) => file.type.startsWith('image/'))
  }, [newFiles])

  /**
   * A combined list of all images to be displayed in the UI.
   * This includes both the visible existing images and the new image files selected by the user.
   */
  const displayedImages = useMemo<(ExistingImage | File)[]>(
    () => [...visibleExistingImages, ...imageFiles],
    [visibleExistingImages, imageFiles]
  )

  /**
   * Handles the 'drop' event from the dropzone.
   * It calculates how many new files can be added based on `maxFiles`
   * and updates the form field's state.
   */
  const handleFilesChange = (acceptedFiles: File[]) => {
    const filesCanAdd = maxFiles - displayedImages.length
    if (filesCanAdd <= 0) return

    const filesToAdd = acceptedFiles.slice(0, filesCanAdd)

    if (filesToAdd.length === 0) return

    const currentFiles = newFiles

    const updatedFiles = [...currentFiles, ...filesToAdd]

    if (maxFiles === 1) {
      field.handleChange(updatedFiles[0] ?? null)
    } else {
      field.handleChange(updatedFiles)
    }
  }

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': [], 'image/*': [] },
    onDrop: handleFilesChange
  })

  const handleClick = () => {
    // Trigger file input click
    const input = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement
    input?.click()
  }

  /**
   * Handles the removal of an image or PDF from the displayed list.
   * It distinguishes between an "existing" image and a "new" file.
   */
  const handleRemove = (indexToRemove: number) => {
    // Check if it's an existing image (has id property)
    if (indexToRemove < visibleExistingImages.length) {
      const itemToRemove = visibleExistingImages[indexToRemove]
      // If the item has an 'id', it's an ExistingImage.
      // Add its ID to the removed list to hide it and notify the parent form.
      setRemovedExistingImages((prev) => [...prev, itemToRemove.id])
      onDeleteExistingImage?.(itemToRemove.id)
      return
    }

    // Calculate the index in the newFiles array
    const newFileIndex = indexToRemove - visibleExistingImages.length

    // Check if it's a PDF or an image
    if (newFileIndex < imageFiles.length) {
      // It's an image file
      const fileToRemove = imageFiles[newFileIndex]
      const updatedFiles = newFiles.filter((file) => file !== fileToRemove)
      if (maxFiles === 1) {
        field.handleChange(updatedFiles[0] ?? null)
      } else {
        field.handleChange(updatedFiles)
      }
    } else {
      // It's a PDF file
      const pdfIndex = newFileIndex - imageFiles.length
      const fileToRemove = pdfFiles[pdfIndex]
      const updatedFiles = newFiles.filter((file) => file !== fileToRemove)
      if (maxFiles === 1) {
        field.handleChange(updatedFiles[0] ?? null)
      } else {
        field.handleChange(updatedFiles)
      }
    }
  }

  // Determines if the upload dropzone should be visible.
  const totalFiles = displayedImages.length + pdfFiles.length
  const canUpload = totalFiles < maxFiles
  // Determines if the dropzone should take up the full width (when no images are present).
  const showOverlay = fullWidth && totalFiles === 0

  // Helper function to get file index for removal
  // The index represents position in the combined array: [existingImages, imageFiles, pdfFiles]
  const getFileIndex = (file: File, isPdf: boolean) => {
    if (isPdf) {
      // PDFs come after existing images and image files
      return (
        visibleExistingImages.length +
        imageFiles.length +
        pdfFiles.indexOf(file)
      )
    }
    // Images come after existing images
    return visibleExistingImages.length + imageFiles.indexOf(file)
  }

  return (
    <FieldWrapper field={field} label={label} required={required}>
      <div data-invalid={isInvalid || undefined}>
        <div className="flex w-full flex-wrap items-center gap-4">
          {/* PDF Badges */}
          <AnimatePresence mode="popLayout">
            {pdfFiles.map((pdfFile) => {
              const fileIdx = getFileIndex(pdfFile, true)
              return (
                <motion.div
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative"
                  exit={{ opacity: 0, scale: 0.0 }}
                  initial={{ opacity: 0, scale: 0.0 }}
                  key={`pdf-${pdfFile.name}-${pdfFile.size}`}
                  layout
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <div className="bg-muted border-input flex items-center gap-2 rounded-lg border px-3 py-2">
                    <span className="text-muted-foreground text-sm font-medium">
                      {pdfFile.name}
                    </span>
                    {allowDelete && (
                      <button
                        className="text-muted-foreground hover:text-destructive flex h-4 w-4 cursor-pointer items-center justify-center rounded-full transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove(fileIdx)
                        }}
                        type="button"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>

        {/* Image Previews */}
        <ImagePreview
          onDelete={allowDelete ? handleRemove : undefined}
          source={displayedImages}
        >
          {(imagePreviews, onThumbnailClick) => (
            <AnimatePresence mode="popLayout">
              {imagePreviews.map((preview, idx) => (
                <motion.div
                  animate={{ opacity: 1, scale: 1 }}
                  className={cn('relative w-fit', sizeClasses[size])}
                  exit={{ opacity: 0, scale: 0.0 }}
                  initial={{ opacity: 0, scale: 0.0 }}
                  key={preview.id}
                  layout
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <div
                    className={`${sizeClasses[size]} ${radiusClasses[radius]} cursor-pointer overflow-hidden`}
                    onClick={() => onThumbnailClick(idx)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && onThumbnailClick(idx)
                    }
                    role="button"
                    tabIndex={0}
                  >
                    {/* The HeroUI Image component is used here. For external image URLs from a server,
                    ensure the hostname is configured in `next.config.js` under the `images.remotePatterns` key. */}
                    <Image
                      alt={t('previewAlt', { index: (idx + 1).toString() })}
                      className={cn(
                        'aspect-square object-cover',
                        isInvalid && 'border-danger border-2'
                      )}
                      height={sizeValues[size]}
                      src={preview.url}
                      unoptimized
                      width={sizeValues[size]}
                    />
                  </div>
                  {allowDelete && (
                    <button
                      className="bg-destructive/80 absolute -end-2 -top-2 z-10 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-white"
                      onClick={() => handleRemove(idx)}
                      type="button"
                    >
                      <X size={12} />
                    </button>
                  )}
                </motion.div>
              ))}

              {canUpload && (
                <motion.div
                  animate={{ opacity: 1, scale: 1 }}
                  className={cn(
                    'w-full',
                    height && showOverlay && `h-[${height}px]`
                  )}
                  exit={{ opacity: 0, scale: 0.0 }}
                  initial={{ opacity: 0, scale: 0.0 }}
                  key="uploader"
                  layout
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <button
                    {...getRootProps()}
                    className={cn(
                      'border-input bg-background flex h-[42px] w-full cursor-pointer items-center rounded-lg border-1 border-dashed transition-colors',
                      isDragActive && 'border-primary bg-primary/5',
                      isInvalid && 'border-destructive border-solid',
                      'hover:bg-accent'
                    )}
                    data-invalid={isInvalid || undefined}
                    onClick={handleClick}
                    style={{ borderRadius: '7px' }}
                    // Default "submit" would submit the enclosing form.
                    type="button"
                  >
                    <input {...getInputProps()} className="hidden" />

                    {/* Choose file area */}
                    <div className="flex-1 px-3.5 py-2">
                      <span
                        className={cn(
                          'text-muted-foreground text-sm',
                          field.state.value && 'text-foreground'
                        )}
                      >
                        {(() => {
                          const value = field.state.value
                          if (value instanceof File) {
                            return value.name
                          }
                          if (Array.isArray(value) && value.length > 0) {
                            return value[0].name
                          }
                          return t('chooseFile')
                        })()}
                      </span>
                    </div>

                    {/* Upload chip */}
                    <div className="mx-2 flex items-center">
                      <div className="bg-accent-muted text-accent-muted-foreground flex items-center gap-1.5 rounded-full px-3 py-1.5 rtl:flex-row-reverse">
                        {isLoading ? (
                          <svg
                            className="text-destructive h-3.5 w-3.5 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              fill="currentColor"
                            ></path>
                          </svg>
                        ) : icon ? (
                          <span className="text-destructive flex h-3.5 w-3.5 items-center justify-center [&>*]:max-h-full [&>*]:max-w-full">
                            {icon}
                          </span>
                        ) : (
                          <Upload className="text-destructive h-3.5 w-3.5" />
                        )}
                        <span className="text-xs font-medium">
                          {t('uploadButton')}
                        </span>
                      </div>
                    </div>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </ImagePreview>
      </div>
    </FieldWrapper>
  )
}
